import { Button, Checkbox, Input, Modal, Select, message } from "antd";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getKeyAccountSkus,
  setKeyAccountSkus,
} from "../../../api/internal_tool_endpoints";
import { foldSearch } from "../../../utils/searchFold";

// The caps the server and the Key Accounts page apply.
const MAX_GROUPS = 6;
const MAX_SKUS = 20;
const MAX_LABEL = 40;
const MAX_KEY = 300;

// A C0 or C1 control character, or DEL: the characters the server refuses.
const hasControl = (s) =>
  [...s].some((c) => {
    const code = c.charCodeAt(0);
    return code < 32 || (code >= 127 && code < 160);
  });

/** The SKU keys a textarea lists: one per line, trimmed, blanks and repeats dropped. */
const skuLines = (text) => [
  ...new Set(
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
  ),
];

const blankGroup = () => ({ label: "", skus: "", rest: false });

/** A stored group as the editor holds it, its SKUs one per line. */
const toDraft = (group) => ({
  label: group.label ?? "",
  skus: (group.subbrands ?? []).join("\n"),
  rest: group.rest === true,
});

/** Why one group cannot be saved, or null. Mirrors the server's check. */
function groupError(draft, restCount) {
  const label = draft.label.trim();
  if (!label) return "Give the group a name.";
  if (label.length > MAX_LABEL) return `Use at most ${MAX_LABEL} characters.`;
  if (hasControl(label)) return "Remove the control character.";
  if (draft.rest) {
    return restCount > 1 ? "Only one group can hold every other SKU." : null;
  }
  const skus = skuLines(draft.skus);
  if (skus.length === 0) {
    return "List at least one SKU, or tick Every other SKU.";
  }
  if (skus.length > MAX_SKUS) return `List at most ${MAX_SKUS} SKUs.`;
  if (skus.some((key) => key.length > MAX_KEY || hasControl(key))) {
    return `A SKU key is one line of at most ${MAX_KEY} characters.`;
  }
  return null;
}

/**
 * Set the SKU groups a workspace's Key Accounts page uses. They live under
 * `workspace_entitlements/{id_client}.key_accounts_skus`. A group names
 * subbrands by their `brand|subbrand` key, or holds every SKU no other group
 * names. The named SKUs lead the Details SKU grid in group order, and each
 * group adds a "No <group>" chip. Save replaces the groups whole, and saving
 * none removes the setting.
 *
 * @param {boolean}  open
 * @param {string[]} workspaces - id_clients to choose from.
 * @param {Function} onCancel
 */
const ManageKeyAccountSkusModal = ({ open, workspaces, onCancel }) => {
  const [idClient, setIdClient] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // Counts workspace reads. Only the newest read may fill the editor.
  const generationRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    generationRef.current += 1;
    setIdClient(null);
    setGroups([]);
    setLoading(false);
    setLoadFailed(false);
  }, [open]);

  // A failed read locks the editor, so staff cannot save over groups they
  // never saw. A read a later pick or a reopen has superseded changes nothing.
  const selectWorkspace = async (ws) => {
    const gen = ++generationRef.current;
    const isStale = () => generationRef.current !== gen;
    setIdClient(ws);
    setGroups([]);
    setLoadFailed(false);
    setLoading(true);
    try {
      const stored = await getKeyAccountSkus(ws);
      if (isStale()) return;
      setGroups((stored?.groups ?? []).map(toDraft));
    } catch (e) {
      if (isStale()) return;
      setLoadFailed(true);
      message.error(e.message || "Failed to load SKU groups.");
    }
    setLoading(false);
  };

  const restCount = groups.filter((g) => g.rest).length;
  const errors = groups.map((g) => groupError(g, restCount));
  const invalid = errors.some(Boolean);
  const locked = loading || loadFailed;

  const update = (index, patch) =>
    setGroups((gs) => gs.map((g, i) => (i === index ? { ...g, ...patch } : g)));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setKeyAccountSkus({
        id_client: idClient,
        groups: groups.map((g) => ({
          label: g.label.trim(),
          subbrands: g.rest ? [] : skuLines(g.skus),
          rest: g.rest,
        })),
      });
      message.success(`SKU groups updated for ${idClient}.`);
      onCancel();
    } catch (e) {
      message.error(e.message || "Failed to update SKU groups.");
    } finally {
      setSaving(false);
    }
  };

  const options = useMemo(
    () => (workspaces || []).map((w) => ({ value: w, label: w })),
    [workspaces],
  );

  return (
    <Modal
      title="SKU groups (Key Accounts)"
      open={open}
      onCancel={onCancel}
      width={640}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={!idClient || locked || invalid}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]}
    >
      <div className="mb-3">
        <div className="text-[0.8rem] text-zinc-500 mb-1">Workspace</div>
        <Select
          showSearch
          placeholder="Select a workspace"
          value={idClient}
          onChange={selectWorkspace}
          options={options}
          style={{ width: "100%" }}
          aria-label="Workspace"
          filterOption={(q, opt) =>
            foldSearch(opt?.label).includes(foldSearch(q))
          }
        />
      </div>

      {!idClient ? (
        <p className="text-[0.8rem] text-zinc-400 italic">
          Select a workspace to view and edit its SKU groups.
        </p>
      ) : loadFailed ? (
        <p className="text-[0.8rem] text-red-600">
          The SKU groups could not load. Pick the workspace again to retry.
        </p>
      ) : (
        <>
          <p className="text-[0.8rem] text-zinc-500 mb-3">
            Named SKUs lead the Details SKU grid in group order, and each group
            adds a &ldquo;No &lt;group&gt;&rdquo; chip. Name a SKU by its
            brand|subbrand key, as in &ldquo;Red Bull|Red Bull Energy
            Drink&rdquo;. With no groups the grid lists the most-listed SKUs
            first.
          </p>
          {groups.length === 0 && !loading && (
            <p className="text-[0.8rem] text-zinc-400 italic mb-3">
              No groups. Saving now removes the setting.
            </p>
          )}
          {groups.map((g, i) => {
            const n = i + 1;
            return (
              <fieldset
                key={n}
                aria-label={`Group ${n}`}
                className="mb-3 rounded border border-zinc-200 p-3"
              >
                <div className="flex items-end gap-2 mb-2">
                  <div className="flex-1">
                    <label
                      htmlFor={`sku-group-${n}-label`}
                      className="block text-[0.8rem] font-medium text-zinc-700 mb-1"
                    >
                      Name
                    </label>
                    <Input
                      id={`sku-group-${n}-label`}
                      value={g.label}
                      disabled={locked}
                      onChange={(e) => update(i, { label: e.target.value })}
                    />
                  </div>
                  <Checkbox
                    checked={g.rest}
                    disabled={locked}
                    onChange={(e) => update(i, { rest: e.target.checked })}
                  >
                    Every other SKU
                  </Checkbox>
                  <Button
                    aria-label={`Remove group ${n}`}
                    disabled={locked}
                    onClick={() =>
                      setGroups((gs) => gs.filter((_, j) => j !== i))
                    }
                  >
                    Remove
                  </Button>
                </div>
                <label
                  htmlFor={`sku-group-${n}-skus`}
                  className="block text-[0.8rem] font-medium text-zinc-700 mb-1"
                >
                  SKUs, one per line
                </label>
                <Input.TextArea
                  id={`sku-group-${n}-skus`}
                  value={g.skus}
                  rows={3}
                  disabled={locked || g.rest}
                  status={errors[i] ? "error" : undefined}
                  onChange={(e) => update(i, { skus: e.target.value })}
                />
                {errors[i] && (
                  <div className="text-[0.75rem] mt-1 text-red-600">
                    {errors[i]}
                  </div>
                )}
              </fieldset>
            );
          })}
          <Button
            disabled={locked || groups.length >= MAX_GROUPS}
            onClick={() => setGroups((gs) => [...gs, blankGroup()])}
          >
            Add group
          </Button>
        </>
      )}
    </Modal>
  );
};

ManageKeyAccountSkusModal.propTypes = {
  open: PropTypes.bool.isRequired,
  workspaces: PropTypes.arrayOf(PropTypes.string),
  onCancel: PropTypes.func.isRequired,
};

export default ManageKeyAccountSkusModal;
